import { useEffect, useMemo, useRef, useState } from 'react';
import { PHASES_OF_PLAY, PHASE_OF_PLAY_META } from '@4cm/core/phaseOfPlay';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { Card, ChipStrip, GlassPill, Sheet, type Chip } from './primitives';
import { useDrillLibrary, type LibraryDrill } from '../lib/queries/useDrillLibrary';
import { useVoiceToText } from '../lib/voiceInput';
import { addDrillToSession, type AddDrillResult } from '../lib/sessionDrillAdd';
import {
  rankByFit,
  type FitDrillInput,
  type FitSessionInput,
} from '../lib/drillFitScore';
import './DrillPickerSheet.css';

interface DrillPickerSheetProps {
  /** Open / closed. Parent owns the state so the same sheet instance
   *  can be re-opened without remounting (preserves search query +
   *  scroll position between opens). */
  open: boolean;
  /** Close request. */
  onClose: () => void;
  /** The session we're adding into. Used for "best fit" ranking + the
   *  session_drills INSERT. */
  sessionId: string;
  /** Session metadata for the local fit ranker. Pass through whatever
   *  SessionDetail already has — phase_coverage / objectives / intent. */
  sessionFit: FitSessionInput;
  /** Drill ids already on the session — those rows render with an
   *  "Already added" pill instead of an Add button. */
  alreadyAddedDrillIds: ReadonlySet<string>;
  /** Called when a drill has been successfully added (or was already
   *  present). The parent uses this to refetch session_drills + show
   *  a toast. */
  onAdded: (drill: LibraryDrill, result: AddDrillResult) => void;
}

type SortMode = 'fit' | 'alphabetical';

export function DrillPickerSheet({
  open,
  onClose,
  sessionId,
  sessionFit,
  alreadyAddedDrillIds,
  onAdded,
}: DrillPickerSheetProps) {
  const [search, setSearch] = useState('');
  const [phase, setPhase] = useState<PhaseOfPlay | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('fit');
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { drills, state, source } = useDrillLibrary({ phase, limit: 80 });

  // Voice-to-text for the search input. The mic mirrors the global
  // voice prefs — hidden when muted by the SpeakButton pattern.
  const voice = useVoiceToText({
    onTranscript: (t) => {
      setSearch((prev) => (prev ? `${prev} ${t}` : t).trim());
    },
  });

  // Auto-focus the search input on open so a coach can start typing
  // (or talking) immediately.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(id);
  }, [open]);

  const phaseChips: Chip[] = useMemo(() => {
    return [
      { label: 'All', value: '__all', active: phase === null },
      ...PHASES_OF_PLAY.map((p) => ({
        label: PHASE_OF_PLAY_META[p].label,
        value: p,
        active: phase === p,
      })),
    ];
  }, [phase]);

  const filtered = useMemo(() => {
    if (!drills) return [] as Array<LibraryDrill & FitDrillInput>;
    const q = search.trim().toLowerCase();
    const matches = drills.filter((d) => {
      if (!q) return true;
      const hay = [
        d.name ?? '',
        d.description ?? '',
        ...(d.sub_themes ?? []),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
    return matches as Array<LibraryDrill & FitDrillInput>;
  }, [drills, search]);

  const ordered = useMemo(() => {
    if (sortMode === 'alphabetical') {
      return [...filtered].sort((a, b) =>
        (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase()),
      );
    }
    return rankByFit(filtered, sessionFit);
  }, [filtered, sortMode, sessionFit]);

  const handleAdd = async (drill: LibraryDrill) => {
    if (adding) return;
    setAdding(drill.id);
    setError(null);
    const result = await addDrillToSession({ sessionId, drillId: drill.id });
    setAdding(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onAdded(drill, result);
  };

  return (
    <Sheet
      side="right"
      open={open}
      label="Add a drill from your library"
      className="drill-picker-sheet"
    >
      <header className="drill-picker-sheet__head">
        <div>
          <span className="drill-picker-sheet__eyebrow">Library</span>
          <h2 className="drill-picker-sheet__title">Add a drill</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="drill-picker-sheet__close"
          aria-label="Close drill picker"
        >
          ×
        </button>
      </header>

      <div className="drill-picker-sheet__search">
        <input
          ref={inputRef}
          type="search"
          className="drill-picker-sheet__input"
          placeholder="Search drills (or hit the mic)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
          aria-label="Search drills"
        />
        <button
          type="button"
          className="drill-picker-sheet__mic"
          onClick={() => (voice.listening ? voice.stop() : void voice.start())}
          data-listening={voice.listening || undefined}
          aria-label={voice.listening ? 'Stop listening' : 'Search by voice'}
          aria-pressed={voice.listening}
        >
          {voice.listening ? '◼' : '🎤'}
        </button>
      </div>

      <ChipStrip chips={phaseChips} onSelect={(v) => setPhase(v === '__all' ? null : (v as PhaseOfPlay))} />

      <div className="drill-picker-sheet__sort" role="tablist" aria-label="Sort">
        <button
          type="button"
          role="tab"
          aria-selected={sortMode === 'fit'}
          className="drill-picker-sheet__sort-tab"
          data-active={sortMode === 'fit' || undefined}
          onClick={() => setSortMode('fit')}
        >
          Best fit
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sortMode === 'alphabetical'}
          className="drill-picker-sheet__sort-tab"
          data-active={sortMode === 'alphabetical' || undefined}
          onClick={() => setSortMode('alphabetical')}
        >
          A–Z
        </button>
      </div>

      {error && (
        <div className="drill-picker-sheet__error" role="alert">
          {error}
        </div>
      )}

      {state === 'loading' ? (
        <p className="drill-picker-sheet__status">Loading drills…</p>
      ) : ordered.length === 0 ? (
        <p className="drill-picker-sheet__status">
          {search.trim()
            ? 'No matches. Try a different search or clear the filter.'
            : "No drills in the library yet. Author one in /drills/new."}
        </p>
      ) : (
        <ul className="drill-picker-sheet__list" aria-label="Drills">
          {ordered.map((drill) => {
            const already = alreadyAddedDrillIds.has(drill.id);
            const isAdding = adding === drill.id;
            return (
              <li key={drill.id}>
                <Card variant="opaque" className="drill-picker-card">
                  <div className="drill-picker-card__head">
                    <h3 className="drill-picker-card__title">
                      {drill.name ?? 'Untitled drill'}
                    </h3>
                    {drill.primary_phase && (
                      <GlassPill mono>
                        <span aria-hidden="true">
                          {PHASE_OF_PLAY_META[drill.primary_phase].glyph}
                        </span>
                        {PHASE_OF_PLAY_META[drill.primary_phase].label}
                      </GlassPill>
                    )}
                  </div>
                  {drill.description && (
                    <p className="drill-picker-card__desc">{drill.description}</p>
                  )}
                  <div className="drill-picker-card__foot">
                    <div className="drill-picker-card__themes">
                      {drill.sub_themes.slice(0, 3).map((t) => (
                        <GlassPill key={t} mono>{t}</GlassPill>
                      ))}
                      {typeof drill.duration_mins === 'number' && (
                        <GlassPill mono>{drill.duration_mins} min</GlassPill>
                      )}
                    </div>
                    {already ? (
                      <span className="drill-picker-card__already">On session</span>
                    ) : (
                      <button
                        type="button"
                        className="drill-picker-card__add"
                        onClick={() => void handleAdd(drill)}
                        disabled={isAdding}
                      >
                        {isAdding ? 'Adding…' : '+ Add'}
                      </button>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {source === 'seed' && (
        <p className="drill-picker-sheet__hint">
          Showing the offline starter library — connect to see your full library.
        </p>
      )}
    </Sheet>
  );
}
